import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { PanelToolbar } from '../../components/panels/PanelToolbar';
import { useAdmin } from '../../context/admin';
import { useAuth } from '../../context/auth';
import { ApiError, foldersApi } from '../../lib/api';
import { saveLayout } from '../../lib/api/admin';
import type { NavTab } from '../../lib/nav';
import type { Folder } from '../../lib/types';
import { asSavedLayout, type Preset, type SavedLayout } from '../logview/layout';
import { SkeletonLog } from '../../components/ui/Skeleton';

// The viewer brings the canvas drawing and the file parsers; load it on demand.
const LogViewer = lazy(async () => ({
  default: (await import('../logview/LogViewer')).LogViewer,
}));

interface Props {
  wellId: number;
  /** The tab this screen is open in: its id scopes the arrangement, its config is the role default. */
  tab: NavTab;
  preset: Preset;
  title: string;
}

/**
 * A log tab drawn from the well's LAS and ASCII files. The superadmin arranges
 * it and saves that as the default of the layout in use, which is the layout
 * of the role being viewed: everyone on that role then opens it that way.
 */
export function LogScreen({ wellId, tab, preset, title }: Props) {
  const { user } = useAuth();
  const { access, store, commit } = useAdmin();
  const [tree, setTree] = useState<Folder[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    foldersApi
      .tree(wellId)
      .then((res) => {
        if (!cancelled) setTree(res.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load the well data files.');
      });
    return () => {
      cancelled = true;
    };
  }, [wellId]);

  const roleDefault = useMemo(() => asSavedLayout(tab.config?.log), [tab.config]);

  // The layout the default goes to. A superadmin viewing as someone saves to
  // that person's role's layout, which is the point of viewing as them.
  const target = store.layouts.find((l) => l.id === access.layoutId);
  const maySave = user?.role === 'superadmin' && Boolean(target?.rails.some((r) => r.tabs.some((t) => t.id === tab.id)));

  const saveDefault = useCallback(
    async (layout: SavedLayout) => {
      if (!target) return;
      const rails = target.rails.map((r) => ({
        ...r,
        tabs: r.tabs.map((t) => (t.id === tab.id ? { ...t, config: { ...t.config, log: layout } } : t)),
      }));
      await commit(saveLayout(target.id, target.name, { rails }));
    },
    [target, tab.id, commit],
  );

  return (
    <section className="panel panel--full" aria-label={tab.label}>
      <PanelToolbar title={title} accent />
      {error ? (
        <div className="alert alert--page" role="alert">
          {error}
        </div>
      ) : tree ? (
        <Suspense fallback={<SkeletonLog />}>
          <LogViewer
            wellId={wellId}
            tree={tree}
            preset={preset}
            scope={`${preset}.${tab.id}`}
            roleDefault={roleDefault}
            onSaveDefault={maySave ? saveDefault : undefined}
            defaultTarget={target?.name}
          />
        </Suspense>
      ) : (
        <SkeletonLog />
      )}
    </section>
  );
}
